/**
 * URL Validation Utility for Regtank Verification Links
 * Prevents open redirect vulnerabilities by validating URLs before navigation
 */

// Whitelist of allowed Regtank domains (covers both liveness verification and onboarding)
const ALLOWED_REGTANK_DOMAINS = [
  'verify.regtank.com',
  'regtank.com',
  'api.regtank.com',
  'onboarding.regtank.com',
] as const;

/**
 * Validates a URL to ensure it's a legitimate Regtank verification link
 *
 * Security checks:
 * 1. Must be a valid URL format
 * 2. Must use HTTPS protocol
 * 3. Domain must be in whitelist
 *
 * @param url - The URL to validate
 * @returns true if URL is valid and safe to redirect to, false otherwise
 */
export const isValidRegtankUrl = (url: string): boolean => {
  if (!url) {
    console.error('Invalid Regtank URL: URL is empty');
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Check 1: Must use HTTPS (not HTTP)
    if (urlObj.protocol !== 'https:') {
      console.error('Invalid Regtank URL: must use HTTPS protocol');
      return false;
    }

    // Check 2: Domain must be in whitelist
    const isAllowedDomain = ALLOWED_REGTANK_DOMAINS.some(
      (domain) => urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );

    if (!isAllowedDomain) {
      console.error(
        `Invalid Regtank URL: domain '${urlObj.hostname}' is not whitelisted. Allowed domains: ${ALLOWED_REGTANK_DOMAINS.join(', ')}`
      );
      return false;
    }

    return true;
  } catch (error) {
    console.error('Invalid Regtank URL format:', error);
    return false;
  }
};